import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Sentry } from '@/shared/lib/sentry';
import { logger } from '@/shared/lib/logger';
import * as s from './ErrorBoundary.css';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  /**
   * "다시 시도" 동작을 갈아끼운다. 기본값은 상태 리셋(= children 재렌더)인데,
   * 라우터 트리 전체가 죽은 최상위 바운더리에서는 재렌더가 곧바로 재throw 를 부른다.
   * 그 위치에서는 `() => window.location.reload()` 를 넘겨 전체 리로드로 복구한다.
   */
  onRetry?: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/** 정규화로 만들어낸 Error — 원본이 Error 가 아니었음을 표시한다. */
class NonErrorThrown extends Error {}

/**
 * React 는 throw 된 값을 가공 없이 바운더리에 넘긴다 — Error 가 아닐 수 있다.
 * 실제 사례: TanStack Router 의 `throw router.getMatch(id)?.…` 가 `throw undefined` 가 된다.
 * 정규화하지 않으면 `captureException(undefined)` 가 나가 내용 없는 Sentry 이벤트가 된다.
 * 다만 이 문구는 Sentry 용이라 화면에는 노출하지 않는다(render 참조).
 */
function toError(value: unknown): Error {
  return value instanceof Error ? value : new NonErrorThrown(`Non-Error thrown: ${String(value)}`);
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
    };
  }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return {
      hasError: true,
      error: toError(error),
    };
  }

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    const normalized = toError(error);
    // Sentry에 에러 전송
    Sentry.captureException(normalized, {
      tags: {
        error_boundary: true,
      },
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
    });
    logger.error('ErrorBoundary caught an error:', normalized, errorInfo);
  }

  handleRetry = (): void => {
    if (this.props.onRetry) {
      this.props.onRetry();
      return;
    }
    this.setState({
      hasError: false,
      error: null,
    });
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={s.container}>
          <div className={s.content}>
            <div className={s.iconWrapper}>
              <span className={s.icon}>⚠️</span>
            </div>
            <h1 className={s.title}>오류가 발생했습니다</h1>
            <p className={s.description}>
              예기치 않은 오류가 발생했습니다. 페이지를 다시 시도하거나, 문제가 계속되면 관리자에게
              문의해주세요.
            </p>
            {/* 정규화 문구는 사용자에게 의미가 없으므로 감춘다 — Sentry 에는 그대로 간다 */}
            {this.state.error && !(this.state.error instanceof NonErrorThrown) && (
              <div className={s.errorDetails}>
                <pre className={s.errorMessage}>{this.state.error.message}</pre>
              </div>
            )}
            <button onClick={this.handleRetry} className={s.retryButton}>
              다시 시도
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
